// navigation/nav-config.ts
// Single source of truth for the Core Nav: feature ids, the route-id each maps
// to, section grouping, lucide icons, and the per-feature lock-screen copy.
// Visual values live in the components; this file is data only.

import {
  Radio, GitBranch, Aperture, Wrench, FlaskConical, Layers,
  BarChart3, Target, FileText, Sparkles, Rocket, Workflow,
  History, Settings, Zap, Users, Tag, KeyRound,
  type LucideIcon,
} from "lucide-react";

// Must match the backend FEATURE_IDS / frontend entitlements map.
export type FeatureId =
  | "brand-monitoring" | "ai-reasoning-path" | "deep-signal"
  | "action-items" | "test-refine" | "brand-controls"
  | "performance" | "prompt-intelligence" | "insights-reports"
  | "upmode-agent" | "brand-publisher" | "ai-workflows";

export type SectionId = "measure" | "optimize" | "track" | "advanced";

export interface NavSubItem {
  /** Route id passed to onNavigate. */
  page: string;
  label: string;
  icon: LucideIcon;
}

export interface NavFeature {
  id: FeatureId;
  label: string;
  icon: LucideIcon;
  /** Route id passed to onNavigate (the app's page string). */
  page: string;
  section: SectionId;
  /** Extra route ids that should also mark this item active. */
  altPages?: string[];
  /** Nested items revealed when this item is active/expanded. */
  children?: NavSubItem[];
  /** When set, the row shows a coral count badge sourced at runtime. */
  badge?: "runCount";
}

export interface NavSectionDef {
  id: SectionId;
  label: string;
  collapsible?: boolean;
}

export const SECTIONS: NavSectionDef[] = [
  { id: "measure",  label: "Measure" },
  { id: "optimize", label: "Optimize" },
  { id: "track",    label: "Track" },
  { id: "advanced", label: "Advanced", collapsible: true },
];

export const FEATURES: NavFeature[] = [
  { id: "brand-monitoring",   label: "Brand Monitoring",   icon: Radio,       page: "dashboard",          section: "measure" },
  { id: "ai-reasoning-path",  label: "AI Reasoning Path",  icon: GitBranch,   page: "cognition",          section: "measure" },
  { id: "deep-signal",        label: "Deep Signal",        icon: Aperture,    page: "deep-signal",        section: "measure" },

  { id: "action-items",       label: "Action Items",       icon: Wrench,      page: "fix-actions",        section: "optimize" },
  { id: "test-refine",        label: "Test & Refine",      icon: FlaskConical, page: "ai-sandbox",        section: "optimize" },
  { id: "brand-controls",     label: "Brand Controls",     icon: Layers,      page: "brand-tools",        section: "optimize" },

  { id: "performance",        label: "Performance",        icon: BarChart3,   page: "analytics-overview", section: "track" },
  { id: "prompt-intelligence", label: "Prompt Intelligence", icon: Target,    page: "prompt-tracking",    section: "track" },
  { id: "insights-reports",   label: "Insights & Reports", icon: FileText,    page: "report-generator",   section: "track" },

  {
    id: "upmode-agent", label: "Upmode Agent", icon: Sparkles,
    page: "upmode-agent", section: "advanced",
  },
  {
    id: "brand-publisher", label: "Brand Publisher", icon: Rocket,
    page: "brand-publisher", section: "advanced",
    children: [{ page: "publication-history", label: "Publication History", icon: History }],
  },
  {
    id: "ai-workflows", label: "AI Workflows", icon: Workflow,
    page: "agents", section: "advanced",
    children: [{ page: "agent-runs", label: "Workflow History", icon: History }],
  },
];

// Admin / footer items (role-gated as today, not part of the locks model).
export interface ManageItem { page: string; label: string; icon: LucideIcon; adminOnly?: boolean; agentOnly?: boolean; }
export const MANAGE_ITEMS: ManageItem[] = [
  { page: "settings",            label: "Settings",        icon: Settings },
  { page: "integrations",        label: "Integrations",    icon: Zap,   agentOnly: true },
  { page: "admin-users",         label: "User Management", icon: Users, adminOnly: true },
  { page: "admin-brand-access",  label: "Brand Access",    icon: Tag,   adminOnly: true },
  { page: "admin-brand-keys",    label: "Brand API Keys",  icon: KeyRound, adminOnly: true },
];

/** Which feature (if any) a given route id belongs to — drives active state. */
export function featureForPage(page: string): NavFeature | undefined {
  return FEATURES.find(
    (f) => f.page === page
      || f.altPages?.includes(page)
      || f.children?.some((c) => c.page === page),
  );
}

// ── Lock-screen content ────────────────────────────────────────────────────
// Authored per feature: what it does and why it's worth unlocking. Shown in the
// LockedFeatureModal. Copy can be refined later; the structure stays.
export interface LockScreen {
  title: string;
  /** One-line value proposition under the title. */
  desc: string;
  /** Optional short bullets of what they get. */
  highlights?: string[];
}

export const LOCK_SCREENS: Record<FeatureId, LockScreen> = {
  "brand-monitoring": {
    title: "Brand Monitoring",
    desc: "See exactly how the major AI models describe your brand, scored and tracked over time so you know where you stand.",
    highlights: ["Per-model perception scores", "Tracked run over run", "The source quotes behind every number"],
  },
  "ai-reasoning-path": {
    title: "AI Reasoning Path",
    desc: "Trace how each model reasons its way to an answer about you, so you can see which sources and associations shape its view.",
    highlights: ["Step-by-step model reasoning", "The associations driving perception", "Where to intervene for the biggest shift"],
  },
  "deep-signal": {
    title: "Deep Signal",
    desc: "Go beneath the score to the strongest signals in your data, the standout quotes, framings, and gaps worth acting on.",
    highlights: ["The signals that move perception most", "Framing and sentiment detail", "Flagged factual gaps to close"],
  },
  "action-items": {
    title: "Action Items",
    desc: "A prioritized, impact-ranked list of the highest-leverage fixes, so you always know what to tackle next.",
    highlights: ["Ranked by likely impact", "Grounded in your latest run", "Clear next step on each item"],
  },
  "test-refine": {
    title: "Test & Refine",
    desc: "Try a messaging change in a sandbox and see how the models would respond before you commit to it.",
    highlights: ["Safe what-if scenarios", "Compare before and after", "Refine messaging with evidence"],
  },
  "brand-controls": {
    title: "Brand Controls",
    desc: "Set the brand facts, voice, and guardrails that keep every analysis and output true to who you are.",
    highlights: ["Canonical brand facts", "Voice and tone guardrails", "Consistent results across the app"],
  },
  "performance": {
    title: "Performance",
    desc: "Watch your AI perception move over time, with the trends and inflection points that matter for the business.",
    highlights: ["Perception trends over time", "What is improving and what is not", "Leadership-ready views"],
  },
  "prompt-intelligence": {
    title: "Prompt Intelligence",
    desc: "Understand which prompts surface your brand, where you show up early, and where there is room to grow.",
    highlights: ["Coverage across real prompts", "Where you appear and where you don't", "Prompts worth adding"],
  },
  "insights-reports": {
    title: "Insights & Reports",
    desc: "Turn your data into a clear story, saved insights and polished reports you can share with the team or the board.",
    highlights: ["Save insights as you find them", "Board-ready reports", "Share with a link"],
  },
  "upmode-agent": {
    title: "Upmode Agent",
    desc: "Your always-on AI analyst. Ask anything about your brand's AI visibility and have it take action, right inside the app.",
    highlights: ["Ask about anything in your data", "Grounded, cited answers", "Takes action, not just talk"],
  },
  "brand-publisher": {
    title: "Brand Publisher",
    desc: "Push content updates straight to your channels and watch the models re-index, closing the loop from insight to published action.",
    highlights: ["Publish to your channels", "Watch models re-index", "Insight to action in one place"],
  },
  "ai-workflows": {
    title: "AI Workflows",
    desc: "Automate the probe, measure, act loop. Set triggers, chain actions, and let Upmode run interventions on a schedule.",
    highlights: ["Trigger-based automation", "Chain multi-step actions", "Runs on your schedule"],
  },
};
